// PAGINA INSTALAȚIILOR: traseele arhitecturii din lanț, trase de motorul de
// pe server (`mep/`) ca soluție `mep`. Arhitectura e cea a firului (sau a
// structurii firului); soluția ei de instalații e ultima cu ea în lineage.
//
// Fără soluție, vizualizatorul arată arhitectura și blocul are butonul de
// generare; cu una, documentul ei (arhitectura plus `MepSegment` și
// `MepEquipment`) e desenat cu stratul instalațiilor în 2D și 3D, iar panoul
// arată verificările, valorile folosite și cantitățile.

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as api from "../api";
import { DoarCitire, useRolProiect } from "../components/rol";
import { useT } from "../i18n";
import { FurnizorInstalatii } from "../instalatii/context";
import { intrariDinRezumat, modelDinDocument } from "../instalatii/dinDocument";
import type { Intrare, ModelInstalatii } from "../instalatii/model";
import PanouInstalatii from "../instalatii/Panou";
import SetariInstalatii from "../instalatii/Setari";
import type { RandInstalatii, SolutieRand } from "../nav/api";
import { useActiuni } from "../nav/actiuni";
import { useRegistru } from "../nav/Registru";
import type { CanonicalDoc } from "../viewer/canonical";
import Vizualizator from "../viewer/Vizualizator";

const GOL: ModelInstalatii = { segmente: [], echipamente: [], verificari: [], intrari: [] };

/** Rândul sub care vizualizatorul arată documentul instalațiilor: arhitectura
 *  lui, cu id-ul soluției `mep` — vizualizatorul desenează documentul dat,
 *  nu cere altul. */
const randPentru = (arh: SolutieRand, mep: RandInstalatii): SolutieRand =>
  ({ ...arh, id: mep.id, lucrari: [], rulare: null, promoted: false, faze: null });

export default function Instalatii() {
  const { projectId = "", sol } = useParams();
  const t = useT();
  const { registru } = useRegistru();
  // rolul se citește AICI, înaintea lui `DoarCitire`: vizualizatorul e numai de
  // citit (nu se editează arhitectura de pe pagina instalațiilor), dar blocul
  // din meniul lui pornește generarea și scrie setările
  const { potScrie } = useRolProiect();
  const rand = registru?.solutii.find((s) => s.id === sol) ?? null;
  const arhId = rand ? (rand.discipline === "architecture" ? rand.id : rand.lineage.architecture ?? null) : null;
  const arh = registru?.solutii.find((s) => s.id === arhId) ?? null;
  const mep = useMemo(() => (registru?.instalatii ?? [])
    .filter((r) => r.arhitectura === arhId)
    .sort((a, b) => b.numar - a.numar)[0] ?? null, [registru, arhId]);

  const [incarcat, setIncarcat] = useState<{ id: string; doc: CanonicalDoc; model: ModelInstalatii } | null>(null);
  const [eroare, setEroare] = useState(false);
  const gata = mep?.status === "ready" ? mep.id : null;
  useEffect(() => {
    if (!gata || incarcat?.id === gata) return;
    let viu = true;
    setEroare(false);
    api.solutionDocument(projectId, gata).then((d) => {
      if (!viu) return;
      const doc = d as unknown as CanonicalDoc;
      setIncarcat({ id: gata, doc, model: modelDinDocument(doc) });
    }, () => { if (viu) setEroare(true); });
    return () => { viu = false; };
  }, [projectId, gata, incarcat?.id]);

  if (!registru) return <div className="studio" />;
  if (!arh) {
    return <div className="studio"><p className="note float" style={{ left: 16, top: 16 }}>
      {t("inst.fara_arhitectura")}</p></div>;
  }
  const arata = incarcat && mep && incarcat.id === mep.id ? incarcat : null;
  const model = arata
    ? { ...arata.model, intrari: intrariDinRezumat(mep!.summary.statistici as Record<string, unknown>,
                                                     (mep!.summary.intrari ?? []) as Intrare[]) }
    : GOL;
  const bloc = <BlocInstalatii projectId={projectId} arh={arh} mep={mep} eroareDoc={eroare} areModel={!!arata}
                               potScrie={potScrie} />;
  return (
    <FurnizorInstalatii model={model}>
      <div className="studio">
        {arata
          ? <DoarCitire>
              <Vizualizator key={arata.id} projectId={projectId} solution={randPentru(arh, mep!)}
                            document={arata.doc} plin vedereInitiala="3d" blocSolutie={bloc} />
            </DoarCitire>
          : <DoarCitire>
              <Vizualizator key={arh.id} projectId={projectId} solution={arh} plin vedereInitiala="3d"
                            blocSolutie={bloc} />
            </DoarCitire>}
      </div>
    </FurnizorInstalatii>
  );
}

/** Blocul din meniul vizualizatorului: starea soluției, «Trage instalațiile»,
 *  setările proiectului și panoul cu verificările. */
function BlocInstalatii({ projectId, arh, mep, eroareDoc, areModel, potScrie }: {
  projectId: string; arh: SolutieRand; mep: RandInstalatii | null; eroareDoc: boolean; areModel: boolean;
  potScrie: boolean;
}) {
  const t = useT();
  const actiuni = useActiuni();
  const cheie = `mep:${arh.id}`;
  const lucreaza = actiuni.ocupat === cheie || mep?.status === "running" || mep?.status === "queued"
    || !!mep?.rulare;
  const genereaza = (setari?: api.SetariInstalatii) =>
    actiuni.pornesteJob(cheie, "mep", { upstream_solution_id: arh.id, ...(setari ? { setari } : {}) });
  const picata = mep?.status === "failed"
    ? String(((mep.summary.eroare ?? {}) as { message?: string }).message ?? "") : null;
  return (
    <div className="inst-panou">
      <div className="inst-antet">
        <b>{mep ? t("inst.solutia", { n: mep.numar }) : t("inst.grup")}</b>
        {potScrie && (
          <button type="button" className="btn btnp" disabled={lucreaza || arh.status !== "ready"}
                  onClick={() => void genereaza()}>
            {lucreaza ? t("inst.in_lucru") : t(mep ? "inst.regenereaza" : "inst.genereaza")}
          </button>
        )}
      </div>
      {!mep && !lucreaza && <p className="note">{t("inst.nicio_solutie")}</p>}
      {picata !== null && <p className="note bad">{t("inst.picata", { motiv: picata })}</p>}
      {actiuni.eroare && <p className="note bad">{actiuni.eroare}</p>}
      {eroareDoc && <p className="note bad">{t("inst.document_necitit")}</p>}
      {potScrie && <SetariInstalatii projectId={projectId} dezactivat={lucreaza}
                                     dupaSalvare={() => void genereaza()} />}
      {areModel && <PanouInstalatii />}
    </div>
  );
}
